"use client"

import { useState, useEffect } from "react"
import "./ServiceManager.css"
import serviceService from "../../../services/api/serviceService"
import serviceCategoryService from "../../../services/api/serviceCategoryService"

export default function ServiceManagement() {
    // State for services list
    const [services, setServices] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // State for form inputs
    const [formData, setFormData] = useState({
        servicename: "",
        description: "",
        content: "",
        service_category: "",
        service_img: "/placeholder.svg?height=100&width=300"
    })

    // State for views
    const [selectedService, setSelectedService] = useState(null)
    const [showDetail, setShowDetail] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)

    // Fetch services and categories on component mount
    useEffect(() => {
        fetchServices()
        fetchCategories()
    }, [])

    // Fetch all services
    const fetchServices = async () => {
        try {
            setLoading(true)
            const response = await serviceService.getAllServices()
            console.log('Phản hồi dịch vụ:', response)
            setServices(Array.isArray(response.services) ? response.services : [])
        } catch (error) {
            console.error("Lỗi khi tải danh sách dịch vụ:", error)
            setError("Không thể tải danh sách dịch vụ")
            setServices([])
        } finally {
            setLoading(false)
        }
    }

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            const response = await serviceCategoryService.getAllCategories()
            setCategories(Array.isArray(response.categories) ? response.categories : [])
        } catch (error) {
            console.error("Lỗi khi tải danh mục dịch vụ:", error)
            setError("Không thể tải danh mục dịch vụ")
        }
    }

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setFormData({
                        ...formData,
                        service_img: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            await serviceService.createService(formData)
            await fetchServices()
            // Reset form
            setFormData({
                servicename: "",
                description: "",
                content: "",
                service_category: "",
                service_img: "/placeholder.svg?height=100&width=300"
            })
            // Hide the form after submission
            setShowAddForm(false)
        } catch (error) {
            console.error("Lỗi khi tạo dịch vụ mới:", error)
            setError("Không thể tạo dịch vụ mới")
        } finally {
            setLoading(false)
        }
    }

    // Handle service deletion
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa dịch vụ này không?")) {
            try {
                setLoading(true)
                await serviceService.deleteService(id)
                await fetchServices()
                if (selectedService && selectedService._id === id) {
                    setShowDetail(false)
                    setSelectedService(null)
                }
            } catch (error) {
                console.error("Lỗi khi xóa dịch vụ:", error)
                setError("Không thể xóa dịch vụ")
            } finally {
                setLoading(false)
            }
        }
    }

    // Handle view details
    const handleViewDetails = async (service) => {
        try {
            setLoading(true)
            console.log('Đang tải thông tin dịch vụ:', service)
            const response = await serviceService.getServiceById(service._id)
            console.log('Đã nhận thông tin chi tiết dịch vụ:', response)
            setSelectedService(response.service)
            setShowDetail(true)
        } catch (error) {
            console.error("Lỗi khi tải thông tin chi tiết dịch vụ:", error)
            console.error("Chi tiết lỗi:", error.response?.data || error.message)
            setError("Không thể tải thông tin chi tiết dịch vụ")
        } finally {
            setLoading(false)
        }
    }

    // Toggle add form
    const toggleAddForm = () => {
        setShowAddForm(!showAddForm)
    }

    return (
        <div className="sm-container">
            {!showDetail ? (
                <>
                    <h1 className="sm-heading">Quản Lý Dịch Vụ</h1>
                    {error && <div className="error-message">{error}</div>}
                    {loading && <div className="loading-spinner">Đang tải...</div>}
                    <div className="sm-layout">
                        <div className="sm-list-container">
                            <div className="sm-list-header">
                                <h2 className="sm-subheading">Danh Sách Dịch Vụ</h2>
                                <button className="sm-add-button" onClick={toggleAddForm} disabled={loading}>
                                    {showAddForm ? "Ẩn Form" : "Thêm Dịch Vụ Mới"}
                                </button>
                            </div>
                            <div className="sm-list">
                                {Array.isArray(services) && services.length > 0 ? (
                                    services.map((service) => (
                                        <div key={service._id} className="sm-card">
                                            <div className="sm-image">
                                                <img src={service.service_img || "/placeholder.svg"} alt={`Ảnh ${service.servicename}`} />
                                            </div>
                                            <div className="sm-details">
                                                <h3>{service.servicename}</h3>
                                                <p><strong>Mô tả:</strong> {service.description}</p>
                                                <p><strong>Nội dung:</strong> {service.content}</p>
                                                <p><strong>Danh mục:</strong> {service.service_category?.name || "Chưa phân loại"}</p>
                                                <div className="sm-actions">
                                                    <button className="sm-view-button" onClick={() => handleViewDetails(service)} disabled={loading}>
                                                        Xem Chi Tiết
                                                    </button>
                                                    <button className="sm-delete-button" onClick={() => handleDelete(service._id)} disabled={loading}>
                                                        Xóa
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-services">Không tìm thấy dịch vụ nào</div>
                                )}
                            </div>
                        </div>

                        {showAddForm && (
                            <div className="sm-add-section">
                                <div className="sm-add-card">
                                    <h2 className="sm-subheading">Thêm Dịch Vụ Mới</h2>
                                    <form onSubmit={handleSubmit} className="sm-form">
                                        <div className="sm-form-group">
                                            <label htmlFor="servicename">Tên Dịch Vụ</label>
                                            <input
                                                type="text"
                                                id="servicename"
                                                name="servicename"
                                                value={formData.servicename}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="sm-form-group">
                                            <label htmlFor="description">Mô tả</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="4"
                                                required
                                            />
                                        </div>

                                        <div className="sm-form-group">
                                            <label htmlFor="content">Nội dung</label>
                                            <textarea
                                                id="content"
                                                name="content"
                                                value={formData.content}
                                                onChange={handleInputChange}
                                                rows="4"
                                                required
                                            />
                                        </div>

                                        <div className="sm-form-group">
                                            <label htmlFor="service_category">Danh mục</label>
                                            <select
                                                id="service_category"
                                                name="service_category"
                                                value={formData.service_category}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Chọn danh mục</option>
                                                {categories.map((category) => (
                                                    <option key={category._id} value={category._id}>{category.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="sm-form-group">
                                            <label htmlFor="service_img">Ảnh Dịch Vụ</label>
                                            <input
                                                type="file"
                                                id="service_img"
                                                name="service_img"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                            <div className="sm-image-preview">
                                                <img src={formData.service_img || "/placeholder.svg"} alt="Xem trước ảnh dịch vụ" />
                                                <p>Ảnh này sẽ hiển thị trong danh sách dịch vụ</p>
                                            </div>
                                        </div>

                                        <div className="sm-form-actions">
                                            <button type="button" className="sm-cancel-button" onClick={toggleAddForm}>
                                                Hủy
                                            </button>
                                            <button type="submit" className="sm-submit-button" disabled={loading}>
                                                {loading ? "Đang thêm..." : "Thêm Dịch Vụ"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {selectedService && (
                            <div className="sm-detail-section">
                                <div className="sm-detail-card">
                                    <h2 className="sm-subheading">Chi Tiết Dịch Vụ</h2>
                                    <div className="sm-detail-image">
                                        <img src={selectedService.service_img || "/placeholder.svg"} alt={`Ảnh ${selectedService.servicename}`} />
                                    </div>
                                    <div className="sm-detail-info">
                                        <div className="sm-info-group">
                                            <label>Tên Dịch Vụ</label>
                                            <div className="sm-info-value">{selectedService.servicename}</div>
                                        </div>
                                        <div className="sm-info-group">
                                            <label>Mô tả</label>
                                            <div className="sm-info-value">{selectedService.description}</div>
                                        </div>
                                        <div className="sm-info-group">
                                            <label>Nội dung</label>
                                            <div className="sm-info-value">{selectedService.content}</div>
                                        </div>
                                        <div className="sm-info-group">
                                            <label>Danh mục</label>
                                            <div className="sm-info-value">{selectedService.service_category?.name || "Chưa phân loại"}</div>
                                        </div>
                                    </div>
                                    <div className="sm-detail-actions">
                                        <button className="sm-back-button" onClick={() => setSelectedService(null)}>
                                            Quay Lại
                                        </button>
                                        <button className="sm-delete-button" onClick={() => handleDelete(selectedService._id)}>
                                            Xóa
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (

                selectedService && (
                    <div className="sm-detail-section">
                        <button className="sm-back-button" onClick={() => setSelectedService(null)} disabled={loading}>Quay Lại</button>
                        <div className="sm-detail-content">
                            <div className="sm-detail-image">
                                <img src={selectedService.service_img || "/placeholder.svg"} alt={`Ảnh ${selectedService.servicename}`} />
                            </div>
                            <div className="sm-detail-info">
                                <h2>{selectedService.servicename}</h2>
                                <p><strong>Mô tả:</strong> {selectedService.description}</p>
                                <p><strong>Nội dung:</strong> {selectedService.content}</p>
                                <p><strong>Danh mục:</strong> {selectedService.service_category?.name || "Chưa phân loại"}</p>
                                <button className="sm-delete-button" onClick={() => { handleDelete(selectedService._id); setSelectedService(null); }} disabled={loading}>
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                )

            )}
        </div>
    )
} 